import { Box, Skeleton, useToast } from '@chakra-ui/react';
import React, { useCallback } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  GetAllPlaidInstitutionAccountsDocumentGQLType,
  GetPlaidLinkTokenDocumentGQLType,
  useConnectPlaidItemMutation,
  useGetAllPlaidInstitutionAccountsQuery,
  useGetPlaidLinkTokenQuery,
  useReconnectPlaidItemMutation,
} from 'borrower/domains/bankIntegrations/requests/plaid.gql.generated';
import { Institution } from 'borrower/domains/bankIntegrations/ui/plaid/Institution';
import { LinkModal } from 'borrower/domains/bankIntegrations/ui/plaid/LinkModal';
import {
  GetBankAccountsByCreditFacilityIdDocumentGQLType,
  GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
} from 'packages/bankAccounts/requests/bankAccounts.gql.generated';
import { errorToastConfig } from 'packages/common-frontend/constants';

import 'source/borrower/borrower.scss';

export const PlaidContainer = ({
  cashBalancePreference,
}: {
  cashBalancePreference: 'current' | 'available';
}) => {
  const toast = useToast();
  const {
    data: plaidLinkData,
    loading: plaidLinkLoading,
  } = useGetPlaidLinkTokenQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  const [
    connectPlaidItemMutate,
    { loading: connectPlaidItemLoading },
  ] = useConnectPlaidItemMutation({
    refetchQueries: [
      GetPlaidLinkTokenDocumentGQLType,
      GetBankAccountsByCreditFacilityIdDocumentGQLType,
      GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
      GetAllPlaidInstitutionAccountsDocumentGQLType,
    ],
  });

  const handleTokenExchange = useCallback(
    async (publicToken: string, plaidInstitutionId: string) => {
      return connectPlaidItemMutate({
        variables: {
          publicToken,
          plaidInstitutionId,
        },
      });
    },
    [connectPlaidItemMutate],
  );

  const [
    reconnectPlaidItem,
    { loading: reconnectPlaidItemLoading },
  ] = useReconnectPlaidItemMutation({
    refetchQueries: [
      GetPlaidLinkTokenDocumentGQLType,
      GetAllPlaidInstitutionAccountsDocumentGQLType,
    ],
  });

  const handleReconnectPlaidItem = useCallback(
    async (plaidItemId: string) => {
      return reconnectPlaidItem({
        variables: {
          plaidItemId,
        },
      });
    },
    [reconnectPlaidItem],
  );

  const {
    data: allPlaidInstitutionAccountsData,
    loading: allPlaidInstitutionAccountsLoading,
  } = useGetAllPlaidInstitutionAccountsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  if (
    connectPlaidItemLoading ||
    reconnectPlaidItemLoading ||
    plaidLinkLoading ||
    !plaidLinkData ||
    allPlaidInstitutionAccountsLoading ||
    !allPlaidInstitutionAccountsData
  ) {
    return <Skeleton height="600px" data-testid="plaid-container-loading" />;
  }

  const {
    token,
    needsUpdate,
    institutionName,
    plaidItemId,
  } = plaidLinkData.plaidLinkToken;
  const { allPlaidInstitutionAccounts } = allPlaidInstitutionAccountsData;

  return (
    <Box>
      <LinkModal
        linkToken={token}
        needsUpdate={needsUpdate}
        institutionName={institutionName}
        plaidItemId={plaidItemId}
        handleTokenExchange={handleTokenExchange}
        handleReconnectPlaidItem={handleReconnectPlaidItem}
      />

      <Box mt="10px">
        {allPlaidInstitutionAccounts.map((institutionWithAccounts) => {
          return (
            <Institution
              cashBalancePreference={cashBalancePreference}
              key={`key-${Math.random()}`}
              institutionWithAccounts={institutionWithAccounts}
            />
          );
        })}
      </Box>
    </Box>
  );
};
